import { useCurrentClient, useDAppKit } from '@mysten/dapp-kit-react'
import { ClientWithCoreApi } from '@mysten/sui/client'
import { Transaction } from '@mysten/sui/transactions'
import { fromBase64 } from '@mysten/sui/utils'
import { useCallback } from 'react'

import { BackendClient } from '@/shared/backend/client'
import { useBackendClient } from '@/shared/kai/contexts/BackendClientContext'
import { getTxData, getTxError, logTxError, TransactionType } from './sentry-utils'
import { toastTxConstructionError, toastTxFailed, toastTxSuccess } from './toast-tx-utils'
import { waitForIndexer } from './waitForIndexer'

export type CreatedObject = {
  objectId: string
  objectType: string
}

export type ExecuteResult = {
  digest: string
  createdObjects?: CreatedObject[]
}

type ExecuteOptions = {
  transactionType: TransactionType
  sentryContext?: Record<string, unknown>
  onSuccess?: (result: ExecuteResult) => void | Promise<void>
  onProcessed?: () => void | Promise<void>
  onError?: () => void
}

const useExecuteWithClient = (client: ClientWithCoreApi, backendClient: BackendClient) => {
  const dAppKit = useDAppKit()

  const signAndExecute = useCallback(
    async (tx: Transaction, options: ExecuteOptions) => {
      const { transactionType, sentryContext, onSuccess, onProcessed, onError } = options

      // Sign in the connected wallet, then broadcast through our own gRPC node.
      let result
      try {
        const { bytes, signature } = await dAppKit.signTransaction({ transaction: tx })
        result = await client.core.executeTransaction({
          transaction: fromBase64(bytes),
          signatures: [signature],
          include: { effects: true, objectTypes: true },
        })
      } catch (error: unknown) {
        onError?.()
        toastTxFailed()
        const txData = await getTxData(tx, client)
        logTxError(transactionType, error, {
          ...sentryContext,
          ...txData,
        })
        return
      }

      const txr = result.Transaction ?? result.FailedTransaction
      const digest = txr.digest

      if (!txr.status.success) {
        onError?.()
        toastTxFailed(digest)
        const txData = await getTxData(tx, client)
        logTxError(
          transactionType,
          getTxError(transactionType, 'execute', txr.status.error.$kind),
          {
            ...sentryContext,
            ...txData,
            txDigest: digest,
            txError: txr.status.error,
          }
        )
        return
      }

      toastTxSuccess(digest)

      const createdObjects: CreatedObject[] = (txr.effects?.changedObjects ?? [])
        .filter(obj => obj.idOperation === 'Created')
        .map(obj => ({ objectId: obj.objectId, objectType: txr.objectTypes?.[obj.objectId] ?? '' }))

      try {
        await onSuccess?.({ digest, createdObjects })
      } catch {
        onError?.()
      }

      // Wait for indexer in background, then call onProcessed
      try {
        await waitForIndexer(backendClient, digest)
        await onProcessed?.()
      } catch {
        // Indexer timeout — proceed anyway
      }
    },
    [dAppKit, client, backendClient]
  )

  const buildAndExecute = useCallback(
    async (buildTx: () => Transaction | Promise<Transaction>, options: ExecuteOptions) => {
      const { transactionType, sentryContext, onError } = options

      let tx: Transaction
      try {
        tx = await buildTx()
      } catch (error) {
        onError?.()
        toastTxConstructionError()
        logTxError(transactionType, error, sentryContext)
        return
      }

      await signAndExecute(tx, options)
    },
    [signAndExecute]
  )

  return buildAndExecute
}

export const useExecute = () => {
  const client = useCurrentClient()
  const backendClient = useBackendClient()
  return useExecuteWithClient(client, backendClient)
}
